import { ActionType, CustomValidators, DEFAULT_BRANDING, LanguageService, Loader, StoreLoader, UnsubscriberClass, ValidationService, ValidationServiceHelper } from "@aston/foundation";
import { FormControlFeedbackComponent, MultiSelectComponent, BtnSwitchComponent } from '@aston/foundation/components';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from "@angular/forms";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { catchError, filter, map, switchMap, takeUntil } from "rxjs/operators";
import { NgbModal, NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslatePipe } from "@ngx-translate/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { toSignal } from "@angular/core/rxjs-interop";
import { EMPTY, from, Subject } from "rxjs";
import { AsyncPipe } from "@angular/common";
import { Router } from "@angular/router";
import cloneDeep from 'lodash/cloneDeep';
import { Store } from "@ngrx/store";

import { FileUploadInstructionsComponent } from "../../../shared-module/components/file-upload-instructions/file-upload-instructions.component";
import { ICreateFederationValidator } from "../../../shared-module/enums/validators/create-federation.validators";
import { ConfirmModalComponent } from "../../../shared-module/components/confirm-modal/confirm-modal.component";
import { FileUploadComponent } from "../../../shared-module/components/file-upload/file-upload.component";
import { InfrastructureService } from "../../../shared-module/services/infrastructure.service";
import { SubsidiariesStoreSelectors } from "../../../root-store/subsidiaries-store";
import { ConsoleDomainValidators } from "../../../shared-module/enums/validators";
import * as AppStoreSelectors from "../../../root-store/app-store/selectors";
import { IMultiSelectConfig } from "../../../shared-module/models";
import { AppConstants } from "../../../app.constants";
import { IFederationForm } from "../../models";

export type FormValidatorFieldsType = ICreateFederationValidator['fields'];

@Component({
	selector: 'federation-form',
	templateUrl: './federation-form.component.html',
	imports: [ReactiveFormsModule, TranslateDirective, FormControlFeedbackComponent, NgSelectComponent, MultiSelectComponent, BtnSwitchComponent, NgbTooltipModule, FileUploadComponent, FileUploadInstructionsComponent, AsyncPipe, TranslatePipe]
})
export class FederationFormComponent extends UnsubscriberClass implements OnInit, OnChanges {
	private store = inject(Store);
	private router = inject(Router);
	private formBuilder = inject(UntypedFormBuilder);
	private languageService = inject(LanguageService);
	private validationService = inject(ValidationService);
	private infrastructureService = inject(InfrastructureService);
	private modalService = inject(NgbModal);


	@Input() value: IFederationForm;

	@Output() cancelForm = new EventEmitter();
	@Output() deleteForm = new EventEmitter<number>();
	@Output() submitForm = new EventEmitter<IFederationForm>();

	form = this.formBuilder.group({});
	fileConfig = AppConstants.FILE_UPLOAD_CONFIGS.TENANT_LOGO;

	private validationHelper: ValidationServiceHelper;
	private rulesetFields: FormValidatorFieldsType;
	lastValue: any = {};

	multiSelectConfig: Partial<IMultiSelectConfig> = {
		minSelect: 2,
		canSelectAll: false,
		canClearSelection: false,
		labelProp: 'tenantName',
		valueProp: 'subsidiaryId',
	};

	customValidationMessages = {
		SubsidiaryIds: {
			minlength: 'Forms.FieldErrorMessages.MinItems'
		}
	}

	currentUserIsSupport = this.store.selectSignal(AppStoreSelectors.selectCurrentUserIsSupport);

	currencies$ = this.languageService.language$.pipe(
		switchMap(lang => this.store.select(AppStoreSelectors.selectCurrencies(lang))),
		map(currencies => currencies.map(c => ({ ...c,  currencyName: `${c.currencyCode} - ${c.currencyName}` })).sort(function(a,b){
			return a.currencyName.localeCompare(b.currencyName);
		})),
	);

	subsidiaries$ = this.store.select(SubsidiariesStoreSelectors.selectSubsidiaries);

	fetchDependencies$ = new Subject<void>()
	fetchDependenciesAsSupport$ = this.fetchDependencies$.pipe(switchMap(_ => this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)), filter(Boolean))
	brandings = toSignal(this.fetchDependenciesAsSupport$.pipe(switchMap(_ => this.infrastructureService.fetchBranding())), { initialValue: []});

	loader = new StoreLoader(
		this.store.select(SubsidiariesStoreSelectors.selectSubsidiariesIsLoading),
		this.store.select(SubsidiariesStoreSelectors.selectSubsidiariesError)
	);

	get isLogoDeletedField() {
		return this.form.get('isLogoDeleted');
	}

	get logoField(): UntypedFormGroup {
		return this.form.get('logo') as UntypedFormGroup;
	}

	get disabled() {
		return this.form.get('federationId')?.disabled;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (this.form && changes.value) {
			this.patchValue({...this.value})
		}
	}

	ngOnInit() {
		this.buildForm();

		this.validationService.getHelper(ConsoleDomainValidators.CreateFederationValidator).pipe(
			takeUntil(this.destroySubscriptions$)
		).subscribe(([helper, fields]) => {
			this.validationHelper = helper;
			this.rulesetFields = fields as FormValidatorFieldsType;
			this.setFormValidators();
			this.fetchDependencies$.next()
		});

		this.patchValue(this.value);
		this.switchEditing(!this.value?.federationId);
	}

	patchValue(federation: IFederationForm, editing?: boolean) {
		this.lastValue = cloneDeep({...federation});

		this.switchEditing(editing);

		this.form.patchValue({
			currencyCode: 'EUR',
			withCustomersAggregated: false,
			...federation
		})
	}

	switchEditing(isEditing: boolean) {
		const opts = { emitEvent: false };
		Object.values(this.form.controls).forEach(c => {
			if (isEditing) c.enable(opts); else c.disable(opts);
		})

		if (this.form.get('federationId')?.value) {
			this.form.get('currencyCode').disable(opts);
			this.form.get('withCustomersAggregated').disable(opts);
		}
	}

	buildForm() {
		this.form.addControl('federationId', new UntypedFormControl());
		this.form.addControl('name', new UntypedFormControl());
		this.form.addControl('currencyCode', new UntypedFormControl());
		this.form.addControl('subsidiaryIds', new UntypedFormControl());
		this.form.addControl('withCustomersAggregated', new UntypedFormControl());
		this.form.addControl('isLogoDeleted', new UntypedFormControl());
		this.form.addControl('logo', this.createFileUploaderFormGroup());
		this.form.addControl('logoUrl', new UntypedFormControl());
		this.form.addControl('brandingCode', new UntypedFormControl(DEFAULT_BRANDING));
	}

	createFileUploaderFormGroup() {
		return this.formBuilder.group({
			fileInfo: this.formBuilder.control(null),
			underlyingFile: this.formBuilder.control(null),
			fileLoader: this.formBuilder.control(new Loader())
		});
	}

	setFormValidators() {
		this.validationHelper.control(this.form.get('name'), this.rulesetFields.Name);
		this.validationHelper.control(this.form.get('withCustomersAggregated'), this.rulesetFields.WithCustomersAggregated);
		this.validationHelper.control(this.form.get('currencyCode'), this.rulesetFields.CurrencyCode);
		this.validationHelper.control(this.form.get('subsidiaryIds'), this.rulesetFields.SubsidiaryIds, [CustomValidators.minimumLength(2)]);
	}

	toggleMerge(originalValue: boolean) {
		const withCustomersAggregated = this.form.get('withCustomersAggregated').value;
		if (withCustomersAggregated !== originalValue) return;
		if (this.disabled) return;

		if (originalValue) {
			// Disabling should be instant
			// Bug 25187: Acc [Console-Consolidations-Identité] La popin de confirmation s'affiche alors que l'on souhaite désactiver le regroupement
			this.form.get('withCustomersAggregated').setValue(false);
			return;
		}

		this.form.get('withCustomersAggregated').setValue(originalValue)
		const modalRef = this.modalService.open(ConfirmModalComponent, {...AppConstants.DEFAULT_MODAL_OPTIONS });
		modalRef.componentInstance.contentConfig = { textsKey: 'Federation.Confirmations.MergeSubsidiaries.' };
		from(modalRef.result).pipe(
			map(result => {
				if (result === ActionType.CANCEL) this.form.get('withCustomersAggregated').setValue(false);
				if (result === ActionType.VALIDATE) this.form.get('withCustomersAggregated').setValue(true);
			}),
			catchError(_ => {
				this.form.get('withCustomersAggregated').setValue(false);
				return EMPTY;
			}),
		).subscribe()
	}

	confirmSubsidiaryChange(originalValue: number[]) {
		if (this.disabled) return;

		const subsidiaries = this.form.get('subsidiaryIds').value;
		this.form.get('subsidiaryIds').setValue(originalValue)
		const modalRef = this.modalService.open(ConfirmModalComponent, {...AppConstants.DEFAULT_MODAL_OPTIONS });
		modalRef.componentInstance.contentConfig = { textsKey: 'Federation.Confirmations.ChangeSubsidiaries.' };
		from(modalRef.result).pipe(
			map(result => {
				if (result === ActionType.CANCEL) {
					this.lastValue.subsidiaryIds = originalValue.slice();
					this.form.get('subsidiaryIds').setValue(originalValue);
				}
				if (result === ActionType.VALIDATE) {
					this.lastValue.subsidiaryIds = subsidiaries.slice();
					this.form.get('subsidiaryIds').setValue(subsidiaries);
					this.onSubmitForm();
				}
			}),
			catchError(_ => {
				this.lastValue.subsidiaryIds = originalValue.slice();
				this.form.get('subsidiaryIds').setValue(originalValue);
				return EMPTY;
			}),
		).subscribe()
	}

	deleteLocalLogo() {
		this.logoField.patchValue({ underlyingFile: null })
		this.isLogoDeletedField.setValue(true)
	}

	onCancel() {
		if (this.form.disabled || !this.lastValue.federationId) return this.cancelForm.next(null);
		this.patchValue(this.lastValue);
		this.isLogoDeletedField.setValue(false)
	}

	onDelete() {
		const values = this.form.value;
		this.deleteForm.next(values.federationId);
	}

	onSubmitForm() {
		const values = {
			...this.form.value,
			// because disabled controls are undefined in form.value
			currencyCode: this.form.get('currencyCode').value,
			subsidiaryIds: this.form.get('subsidiaryIds').value,
			withCustomersAggregated: this.form.get('withCustomersAggregated').value,
		};
		this.submitForm.next({
			federationId: values.federationId,
			name: values.name,
			currencyCode: values.currencyCode,
			subsidiaryIds: values.subsidiaryIds,
			withCustomersAggregated: !!values.withCustomersAggregated,
			logo: values.logo,
			isLogoDeleted: this.lastValue.logo && values.isLogoDeleted,
			logoUrl: (this.lastValue.logo && values.isLogoDeleted) ? null : values.logoUrl,
			brandingCode: (this.currentUserIsSupport() ? this.form.get('brandingCode').value : null) || 'aston_ai',
		});
	}

	onSubmit() {
		if (this.form.get('federationId')?.value) {
			const value = [...this.form.get('subsidiaryIds').value].sort();
			const lastValue = [...this.lastValue.subsidiaryIds].sort();

			if (value.length === lastValue.length && value.every((val, index) => val === lastValue[index])) {
				return this.onSubmitForm();
			}
			this.confirmSubsidiaryChange(this.lastValue.subsidiaryIds);
		} else {
			this.onSubmitForm();
		}
	}
}
